/**
 * Return-URL helpers for guest-mode → login → resume flows.
 *
 * Anonymous visitors browsing a `guestViewAllowed` route may click a sign-in
 * CTA. We carry the current pathname (including the `/{lang}` prefix) through
 * the login flow so the post-login redirect lands the user back where they
 * came from instead of the dashboard.
 *
 * The contract:
 *   - `buildLoginUrlPreservingReturn(path?)` returns `/login?next=<encoded>`
 *     (or `/ar/login?next=...` when the return path is localized). Defaults
 *     to `window.location.pathname + window.location.search` so the calling
 *     component does not need to thread the location through itself.
 *   - `consumeReturnUrl()` reads the `next` parameter from the current URL
 *     and returns it only if it is a safe in-app path (must start with "/"
 *     and not contain "//" or "://"). External redirects are silently
 *     dropped to prevent open-redirect abuse.
 */

const NEXT_PARAM = "next";
const SUPPORTED_LOCALES = new Set(["en", "ar", "de", "es", "fr"]);

/** Returns the path component of an in-app return URL, or null when missing/unsafe. */
export function consumeReturnUrl(search?: string): string | null {
  if (typeof window === "undefined" && !search) return null;
  const raw = search ?? window.location.search;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return null;
  }
  const candidate = params.get(NEXT_PARAM);
  if (!candidate) return null;
  return isSafeReturnPath(candidate) ? candidate : null;
}

/**
 * Returns `true` if `path` is a safe local return URL (begins with `/` and is
 * not a protocol-relative or absolute external URL).
 */
export function isSafeReturnPath(path: string): boolean {
  if (typeof path !== "string") return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("://")) return false;
  return true;
}

/**
 * Build an in-app URL that points at `/login` (or localized `/ar/login`) with
 * the current pathname encoded into the `next` query parameter. Falls back to
 * a bare auth URL when no safe return target is available.
 */
export function buildLoginUrlPreservingReturn(
  returnTo?: string,
  destination: "login" | "register" = "login",
): string {
  const target = resolveReturnTarget(returnTo);
  const base = localizedAuthPath(target, destination);
  if (!target) return base;
  const params = new URLSearchParams();
  params.set(NEXT_PARAM, target);
  return `${base}?${params.toString()}`;
}

export function localizedAuthPath(
  returnTo?: string | null,
  destination: "login" | "register" = "login",
): string {
  const locale = localeFromPath(returnTo);
  return locale ? `/${locale}/${destination}` : `/${destination}`;
}

export function localizedRootPath(path?: string | null): string {
  const locale = localeFromPath(path);
  return locale ? `/${locale}` : "/";
}

function localeFromPath(path?: string | null): string | null {
  if (!path || !isSafeReturnPath(path)) return null;
  const firstSegment = path.split(/[/?#]/)[1];
  return firstSegment && SUPPORTED_LOCALES.has(firstSegment) ? firstSegment : null;
}

function resolveReturnTarget(explicit?: string): string | null {
  if (explicit !== undefined) {
    return isSafeReturnPath(explicit) ? explicit : null;
  }
  if (typeof window === "undefined") return null;
  const path = window.location.pathname + window.location.search;
  return isSafeReturnPath(path) ? path : null;
}
